<template>
  <div class="grid grid-cols-3">
    <VehicleStatus
      v-model:milage="formValues.milage"
      v-model:fuel-level="formValues.fuelLevel"
      :show-milage-not-updated="!car.readyToHandover && !isMilageChanged"
      :show-fuel-level-not-updated="!car.readyToHandover && !isFuelLevelChanged"
      :show-milage-warning="formValues.milage < car.milage"
      class="col-span-2"
      :fuel-type="fueltype"
      @fuel-level-touched="isFuelLevelChanged = true"
    />
  </div>
  <Divider />

  <Damages :car-id="car.id" :booking-id="booking.id" />

  <template v-if="showRentalKeyTag">
    <Divider />

    <h2 class="mb-4">{{ t('rentalKeyTagTitle') }}</h2>
    <StatusBanner v-if="isRegistered" variant="success" class="mb-4">
      {{ t('rentalKeyTagRegistered') }}
    </StatusBanner>
    <p v-else class="mb-4 text-sm text-obsidian-600">
      {{ t('rentalKeyTagDescription') }}
    </p>
  </template>

  <p v-if="waitingForCustomer" class="bg-amber-50 text-amber-800 mt-4 rounded-md p-3 text-sm">
    {{ t('waitingForCustomer') }}
  </p>

  <div class="mt-5 flex flex-col items-end gap-2 max-sm:items-stretch">
    <ButtonNext
      :disabled="waitingForCustomer || disabled || hasUnsentDamages"
      :is-loading="isSaving || isProgramming"
      @click="handleNext"
    >
      {{ hasChanged ? t('saveAndNext') : t('next') }}
    </ButtonNext>
    <CVButton
      v-if="showRentalKeyTag && !isRegistered"
      variant="link"
      size="sm"
      class="max-sm:w-full"
      :disabled="isProgramming || waitingForCustomer || disabled || hasUnsentDamages"
      data-testid="skip-rental-key-tag"
      @click="handleContinueWithoutKeyTag"
    >
      {{ t('continueWithoutKeyTag') }}
    </CVButton>
    <CVButton
      v-else-if="showRentalKeyTag"
      variant="link"
      size="sm"
      class="max-sm:w-full"
      :disabled="isProgramming || waitingForCustomer"
      @click="handleProgramDifferentKey"
    >
      {{ t('programDifferentKeyTag') }}
    </CVButton>
  </div>
</template>

<script lang="ts" setup>
import Damages from '@/components/booking-forms/damages/Damages.vue';
import { useUnsentDamages } from '@/hooks/use-unsent-damages';
import VehicleStatus from '@/components/booking-forms/VehicleStatus.vue';
import ButtonNext from '@/components/buttons/ButtonNext.vue';
import Divider from '@/components/Divider.vue';
import type { CarStatusFormValues } from '@/entities/cars/car.entity';
import { formHasChanged } from '@/hooks/use-form-has-changed';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash';
import { computed, reactive, ref, watch } from 'vue';
import type { Allocation } from '@/entities/allocations/allocation.entity';
import type { Booking } from '@/entities/bookings/booking.entity';
import {
  isRentalKeyTagProgrammingAvailable,
  useRentalKeyTagProgramming,
} from '@/hooks/use-rental-key-tag-programming';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { httpClient } from '@/api/http-client';
import type { RentalKeyTagStatusDto } from '@carvia/ros-client-types';
import { Alert } from '@/services/alert.service';
import CVButton from '@/components/buttons/CVButton.vue';
import StatusBanner from '@/components/StatusBanner.vue';
import { useAbility } from '@casl/vue';

const props = defineProps<{
  booking: Booking;
  allocation: Allocation;
  isSaving?: boolean;
  waitingForCustomer?: boolean;
  disabled?: boolean;
}>();

const { t } = useI18n();
const ability = useAbility();
const isNfcTagWritingEnabled = useFeatureFlag('nfc-tag-writing');

const showRentalKeyTag = computed(
  () =>
    !!isNfcTagWritingEnabled.value &&
    isRentalKeyTagProgrammingAvailable() &&
    ability.can('program', 'RentalKeyTag'),
);
const isRegistered = ref(false);

const car = computed(() => props.allocation.car);
const fueltype = computed(() => car.value.fuelType ?? car.value.vehicleType.fuelType);

// The contract must list every damage, so an Unsent damage holds up the next step.
const { hasUnsentDamages } = useUnsentDamages(() => car.value.id);
const emit = defineEmits<{
  (e: 'onSubmit', value: CarStatusFormValues): void;
}>();

const formValues: CarStatusFormValues = reactive({
  milage: props.allocation.milageStart ?? car.value.milage,
  fuelLevel: props.allocation.fuelLevelStart ?? car.value?.fuelLevel ?? 1,
});

const isMilageChanged = computed(() => formValues.milage !== car.value.milage);
const isFuelLevelChanged = ref(false);

const hasChanged = ref(false);
watch(formValues, async () => {
  hasChanged.value = true;
});

const unchangedValues = ref(cloneDeep(formValues));

const { programTag, isProgramming } = useRentalKeyTagProgramming(() => props.booking.id);

watch(
  showRentalKeyTag,
  async (show) => {
    if (!show) return;
    try {
      const { data } = await httpClient.get<RentalKeyTagStatusDto>(
        `/rental-key-tag/status/${props.booking.id}`,
      );
      if (data.registered) {
        isRegistered.value = true;
      }
    } catch {
      // Keep pending when status cannot be loaded.
    }
  },
  { immediate: true },
);

const submit = () => {
  if (hasUnsentDamages.value) return;
  emit('onSubmit', { ...formValues });
  unchangedValues.value = cloneDeep(formValues);
};

const tryProgramTag = async (): Promise<boolean> => {
  try {
    await programTag();
    isRegistered.value = true;
    return true;
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error && error.code === 'USER_CANCELED') {
      return false;
    }
    const message = error instanceof Error ? error.message : t('rentalKeyTagFailed');
    await Alert.fire({ text: message, icon: 'error' });
    return false;
  }
};

const handleNext = async () => {
  if (showRentalKeyTag.value && !isRegistered.value) {
    if (!(await tryProgramTag())) return;
  }
  submit();
};

const handleProgramDifferentKey = async () => {
  await tryProgramTag();
};

const handleContinueWithoutKeyTag = async () => {
  const { isConfirmed } = await Alert.fire({
    text: t('continueWithoutKeyTagMessage'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('continueWithoutKeyTag'),
    cancelButtonText: t('cancel'),
  });
  if (!isConfirmed) return;
  submit();
};

usePreventLeavingUnsavedForm(() => formHasChanged(unchangedValues.value, formValues));
</script>

<i18n lang="json">
{
  "en": {
    "back": "back to customer overview",
    "waitingForCustomer": "Waiting for the customer to confirm contact details on the counter screen…",
    "saveAndNext": "Save & Next",
    "next": "Next",
    "cancel": "Cancel",
    "rentalKeyTagTitle": "Keychain tag",
    "rentalKeyTagDescription": "Next will program the NFC keychain for this booking.",
    "continueWithoutKeyTag": "Continue without key tag",
    "continueWithoutKeyTagMessage": "The customer will not get a programmed keychain tag for this booking.",
    "rentalKeyTagRegistered": "Key tag programmed",
    "programDifferentKeyTag": "Program a different key",
    "rentalKeyTagFailed": "Key tag programming failed."
  },
  "de": {
    "back": "Zurück zur Kundenübersicht",
    "waitingForCustomer": "Warten auf die Bestätigung der Kontaktdaten durch den Kunden am Schalter…",
    "saveAndNext": "Speichern & Weiter",
    "next": "Weiter",
    "cancel": "Abbrechen",
    "rentalKeyTagTitle": "Schlüsselanhänger",
    "rentalKeyTagDescription": "Weiter programmiert den NFC-Schlüsselanhänger für diese Buchung.",
    "continueWithoutKeyTag": "Ohne Schlüsselanhänger weiter",
    "continueWithoutKeyTagMessage": "Der Kunde erhält für diese Buchung keinen programmierten Schlüsselanhänger.",
    "rentalKeyTagRegistered": "Schlüsselanhänger programmiert.",
    "programDifferentKeyTag": "Anderen Schlüsselanhänger programmieren",
    "rentalKeyTagFailed": "Schlüsselanhänger konnte nicht programmiert werden."
  },
  "es": {
    "back": "volver a la vista general del cliente",
    "waitingForCustomer": "Esperando a que el cliente confirme los datos de contacto en el mostrador…",
    "saveAndNext": "Guardar y continuar",
    "next": "Siguiente",
    "cancel": "Cancelar",
    "rentalKeyTagTitle": "Llavero",
    "rentalKeyTagDescription": "Siguiente programará el llavero NFC para esta reserva.",
    "continueWithoutKeyTag": "Continuar sin llavero",
    "continueWithoutKeyTagMessage": "El cliente no recibirá un llavero programado para esta reserva.",
    "rentalKeyTagRegistered": "Llavero programado",
    "programDifferentKeyTag": "Programar otro llavero",
    "rentalKeyTagFailed": "Error al programar el llavero."
  }
}
</i18n>
